/**
 * ScratchDetailsScreen - Guided multi-step FMEA creation wizard
 * Takes users through: Project → Component → Failure → RPN → Review
 */

import React, { useState, useMemo } from 'react';
import { useWizard } from '../../contexts/WizardContext';
import { useTerminology } from '@/hooks/useTerminology';
import { ProjectDetailsStep } from './scratch/ProjectDetailsStep';
import { ComponentStep } from './scratch/ComponentStep';
import { FailureStep } from './scratch/FailureStep';
import { RPNStep } from './scratch/RPNStep';
import { ReviewStep } from './scratch/ReviewStep';
import './ScratchDetailsScreen.css';

// Form data interface for all steps
export interface ScratchFormData {
  project: {
    name: string;
    description?: string;
    industry?: string;
  };
  component: {
    name: string;
    type: 'SYSTEM' | 'SUBSYSTEM' | 'COMPONENT' | 'PART';
  };
  failure: {
    description: string;
    cause: string;
    category: 'MAN' | 'MACHINE' | 'METHOD' | 'MATERIAL' | 'MEASUREMENT' | 'ENVIRONMENT';
  };
  rpn: {
    severity: number;
    occurrence: number;
    detection: number;
  };
}

const INITIAL_FORM_DATA: ScratchFormData = {
  project: {
    name: '',
    description: '',
    industry: '',
  },
  component: {
    name: '',
    type: 'SYSTEM',
  },
  failure: {
    description: '',
    cause: '',
    category: 'MACHINE',
  },
  rpn: {
    severity: 5,
    occurrence: 5,
    detection: 5,
  },
};

export function ScratchDetailsScreen() {
  const { cancelWizard } = useWizard();
  const { t } = useTerminology();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formData, setFormData] = useState<ScratchFormData>(INITIAL_FORM_DATA);

  const STEPS = useMemo(() => [
    'Project Details',
    'First Component',
    t.failureMode,
    'Risk Assessment',
    'Review'
  ], [t]);

  const handleNext = () => {
    if (currentStepIndex < STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const updateFormData = (updates: Partial<ScratchFormData>) => {
    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const renderStep = () => {
    switch (currentStepIndex) {
      case 0:
        return (
          <ProjectDetailsStep
            data={formData}
            onChange={updateFormData}
            onNext={handleNext}
            onBack={cancelWizard}
          />
        );
      case 1:
        return (
          <ComponentStep
            data={formData}
            onChange={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <FailureStep
            data={formData}
            onChange={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <RPNStep
            data={formData}
            onChange={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return <ReviewStep data={formData} onBack={handleBack} onCancel={cancelWizard} />;
      default:
        return null;
    }
  };

  return (
    <div className="scratch-wizard" data-testid="wizard-scratch-details">
      {/* Progress Stepper */}
      <div className="wizard-stepper">
        {STEPS.map((step, index) => (
          <div
            key={step}
            className={`stepper-step ${
              index === currentStepIndex ? 'active' : index < currentStepIndex ? 'completed' : ''
            }`}
            data-testid={`stepper-step-${index}`}
          >
            <div className="stepper-circle">{index < currentStepIndex ? '✓' : index + 1}</div>
            <div className="stepper-label">{step}</div>
          </div>
        ))}
      </div>

      {/* Current Step Content */}
      <div className="wizard-step-content">{renderStep()}</div>
    </div>
  );
}
